import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ARCHIVE_FILTERS, FILTER_LABEL_KEYS } from './archiveKinds'

/**
 * Segmented filter chips above the archive list. Kinds the student has never answered
 * are hidden once counts are known, so every chip leads somewhere. The highlight pill
 * is measured and slides between chips, matching the Your tasks / Archive tab bar.
 */
export default function ArchiveFilters({ value, counts, showCounts = true, onChange }) {
    const { t, i18n } = useTranslation()
    const isRTL = (i18n.language || '').startsWith('ar')
    const chipRefs = useRef({})
    const rowRef = useRef(null)
    const [indicator, setIndicator] = useState({ x: 0, width: 0, ready: false })
    // Which edges have more chips beyond them; drives the fade that signals the row scrolls.
    const [edges, setEdges] = useState({ start: false, end: false })

    // Until counts arrive only "All" is shown, so chips never pop in and then vanish.
    const visible = ARCHIVE_FILTERS.filter((filter) => filter === 'all' || filter === value || (counts && counts[filter] > 0))
    const showBar = visible.length > 1

    useLayoutEffect(() => {
        const chip = chipRefs.current[value]
        if (!chip) return
        setIndicator((previous) => ({ x: chip.offsetLeft, width: chip.offsetWidth, ready: previous.width > 0 }))
        chip.scrollIntoView?.({ block: 'nearest', inline: 'nearest', behavior: 'smooth' })
    }, [value, counts, showCounts, i18n.language, visible.length])

    const updateEdges = useCallback(() => {
        const row = rowRef.current
        if (!row) return
        // scrollLeft is negative in RTL, so compare magnitudes.
        const scrolled = Math.abs(row.scrollLeft)
        const max = row.scrollWidth - row.clientWidth
        setEdges({ start: scrolled > 2, end: max - scrolled > 2 })
    }, [])

    useEffect(() => {
        updateEdges()
        window.addEventListener('resize', updateEdges)
        return () => window.removeEventListener('resize', updateEdges)
    }, [updateEdges, visible.length, showCounts, i18n.language])

    if (!showBar) return null

    // Roving focus: arrow keys move between chips and select, like a native tablist.
    const onKeyDown = (event) => {
        const index = visible.indexOf(value)
        const step = { ArrowRight: 1, ArrowLeft: -1 }[event.key]
        let next = null
        if (step) next = visible[(index + (isRTL ? -step : step) + visible.length) % visible.length]
        if (event.key === 'Home') next = visible[0]
        if (event.key === 'End') next = visible[visible.length - 1]
        if (!next) return
        event.preventDefault()
        onChange(next)
        chipRefs.current[next]?.focus()
    }

    return (
        <div
            ref={rowRef}
            className={`archive-filters${edges.start ? ' has-start' : ''}${edges.end ? ' has-end' : ''}`}
            role="tablist"
            aria-label={t('archiveFiltersLabel')}
            onKeyDown={onKeyDown}
            onScroll={updateEdges}
        >
            <span
                className={`archive-filter-indicator${indicator.ready ? ' is-ready' : ''}`}
                style={{ width: indicator.width, transform: `translateX(${indicator.x}px)` }}
                aria-hidden="true"
            />
            {visible.map((filter) => {
                const selected = filter === value
                // Counts describe the whole archive, so they are hidden while a search narrows it.
                const count = counts && showCounts ? counts[filter] : null
                return (
                    <button
                        key={filter}
                        type="button"
                        role="tab"
                        aria-selected={selected}
                        tabIndex={selected ? 0 : -1}
                        ref={(element) => {
                            chipRefs.current[filter] = element
                        }}
                        className={`archive-filter${selected ? ' is-selected' : ''}`}
                        onClick={() => onChange(filter)}
                    >
                        <span>{t(FILTER_LABEL_KEYS[filter])}</span>
                        {count > 0 && <span className="archive-filter-count">{count > 999 ? '999+' : count}</span>}
                    </button>
                )
            })}
        </div>
    )
}
